interface Rates {
  earlyWithdraw: number
  bonaship: number
  cash: 0
  bankSavings?: number
  inflation?: number
}

export interface InvestmentParameters {
  monthlyContribution: number
  investmentYears: number
  initialDeposit: number
  rates: Rates
}

export interface InvestmentProjection {
  timeInMonths: number[]
  totalInvested: number[]
  earlyWithdrawValue: (number | null)[]
  bonashipValue: (number | null)[]
  cashValue: number[]
  bankSavingsValue: number[]
  inflationValue: number[]
  rates: Rates
}

/**
 * Converts annual percentage rate to monthly rate, adjusted for inflation if needed
 */
function calculateRealMonthlyRate(annualRate: number): number {
  return Math.pow(1 + annualRate, 1 / 12) - 1
}

/**
 * Calculates investment growth for a single month
 */
function calculateMonthlyGrowth(
  currentValue: number,
  monthlyContribution: number,
  monthlyRate: number,
  isFirstMonth: boolean
): number {
  if (monthlyContribution === 0) {
    return currentValue * (1 + monthlyRate)
  }

  if (isFirstMonth) {
    return currentValue * (1 + monthlyRate)
  }

  return (currentValue + monthlyContribution) * (1 + monthlyRate)
}

/**
 * Calculates total amount invested up to given month
 */
function calculateTotalInvested(
  initialDeposit: number,
  monthlyContribution: number,
  monthIndex: number
): number {
  return initialDeposit + monthlyContribution * (monthIndex + 1)
}

/**
 * Returns month index (starting from 0)
 */
function getMonthIndex(monthIndex: number): number {
  return monthIndex
}

/**
 * Calculates investment projections over time for different investment vehicles
 */
export function calculateInvestmentProjections(
  parameters: InvestmentParameters
): InvestmentProjection {
  const { initialDeposit, rates, investmentYears, monthlyContribution } =
    parameters

  // Calculate real monthly rates (inflation-adjusted)
  const earlyWithdrawMonthlyRate = calculateRealMonthlyRate(rates.earlyWithdraw)
  const bonashipMonthlyRate = calculateRealMonthlyRate(rates.bonaship)
  const cashMonthlyRate = calculateRealMonthlyRate(rates.cash)
  const bankSavingsMonthlyRate = calculateRealMonthlyRate(
    rates.bankSavings || 0
  )
  const inflationRate = calculateRealMonthlyRate(rates.inflation || 0)

  const totalMonths = investmentYears * 12
  const earlyWithdrawEndMonth = 181 // earlyWithdraw ide do 181. mesiaca (vrátane)

  // Initialize result arrays
  const timeInMonths: number[] = []
  const totalInvested: number[] = []

  const earlyWithdrawValue: (number | null)[] = []
  const bonashipValue: (number | null)[] = []
  const cashValue: number[] = []
  const bankSavingsValue: number[] = []
  const inflationValue: number[] = []

  let currentEarlyWithdrawValue = initialDeposit
  let currentBonashipValue = initialDeposit
  let currentCashValue = initialDeposit
  let currentBankSavingsValue = initialDeposit
  let currentInflationValue = initialDeposit

  // Najprv vypočítaj bonashipValue pre mesiac 181, aby sme vedeli kam má earlyWithdraw "vyskoči"
  let bonashipValueAt181 = initialDeposit
  for (let monthIndex = 0; monthIndex < earlyWithdrawEndMonth; monthIndex++) {
    const isFirstMonth = monthIndex === 0
    bonashipValueAt181 = calculateMonthlyGrowth(
      bonashipValueAt181,
      monthlyContribution,
      bonashipMonthlyRate,
      isFirstMonth
    )
  }

  // Phase 1: Calculate všetko do 181 mesiacov (vrátane)
  for (
    let monthIndex = 0;
    monthIndex < Math.min(earlyWithdrawEndMonth, totalMonths);
    monthIndex++
  ) {
    const isFirstMonth = monthIndex === 0

    // Calculate growth for early withdraw
    currentEarlyWithdrawValue = calculateMonthlyGrowth(
      currentEarlyWithdrawValue,
      monthlyContribution,
      earlyWithdrawMonthlyRate,
      isFirstMonth
    )

    // Calculate growth for bonaship (nezávisle)
    currentBonashipValue = calculateMonthlyGrowth(
      currentBonashipValue,
      monthlyContribution,
      bonashipMonthlyRate,
      isFirstMonth
    )

    // Calculate growth for cash
    currentCashValue = calculateMonthlyGrowth(
      currentCashValue,
      monthlyContribution,
      cashMonthlyRate,
      isFirstMonth
    )

    currentBankSavingsValue = calculateMonthlyGrowth(
      currentCashValue,
      monthlyContribution,
      bankSavingsMonthlyRate,
      isFirstMonth
    )

    currentInflationValue = calculateMonthlyGrowth(
      currentInflationValue,
      monthlyContribution,
      inflationRate,
      isFirstMonth
    )

    // V poslednom mesiaci earlyWithdraw "vyskočí" na hodnotu bonaship
    const finalEarlyWithdrawValue =
      monthIndex === earlyWithdrawEndMonth - 1
        ? currentBonashipValue
        : currentEarlyWithdrawValue

    // Store results
    timeInMonths.push(getMonthIndex(monthIndex))
    totalInvested.push(
      calculateTotalInvested(initialDeposit, monthlyContribution, monthIndex)
    )
    earlyWithdrawValue.push(finalEarlyWithdrawValue)
    // Bonaship začína až v mesiaci 181
    bonashipValue.push(
      monthIndex === earlyWithdrawEndMonth - 1 ? currentBonashipValue : null
    )
    cashValue.push(currentCashValue)
    bankSavingsValue.push(currentBankSavingsValue)
    inflationValue.push(currentInflationValue)
  }

  // Phase 2: Pokračuj s bonaship od 182. mesiaca
  if (totalMonths > earlyWithdrawEndMonth) {
    for (
      let monthIndex = earlyWithdrawEndMonth;
      monthIndex < totalMonths;
      monthIndex++
    ) {
      // Calculate growth for bonaship (pokračuje)
      currentBonashipValue = calculateMonthlyGrowth(
        currentBonashipValue,
        monthlyContribution,
        bonashipMonthlyRate,
        false
      )

      // Calculate growth for cash (pokračuje)
      currentCashValue = calculateMonthlyGrowth(
        currentCashValue,
        monthlyContribution,
        cashMonthlyRate,
        false
      )

      currentBankSavingsValue = calculateMonthlyGrowth(
        currentBankSavingsValue,
        monthlyContribution,
        bankSavingsMonthlyRate,
        false
      )

      currentInflationValue = calculateMonthlyGrowth(
        currentInflationValue,
        monthlyContribution,
        inflationRate,
        false
      )

      // Store results
      timeInMonths.push(getMonthIndex(monthIndex))
      totalInvested.push(
        calculateTotalInvested(initialDeposit, monthlyContribution, monthIndex)
      )
      earlyWithdrawValue.push(null) // earlyWithdraw už skončil
      bonashipValue.push(currentBonashipValue)
      cashValue.push(currentCashValue)
      bankSavingsValue.push(currentBankSavingsValue)
      inflationValue.push(currentInflationValue)
    }
  }

  return {
    timeInMonths,
    totalInvested,
    earlyWithdrawValue,
    bonashipValue,
    cashValue,
    bankSavingsValue,
    inflationValue,
    rates,
  }
}
